<template>
	<div class="ai-tools-container">
		<ModalHeader
			:title="getString('ai_images_generator')"
			:button-text="buttonText"
			:button-loading="loadingGeneration"
			:button-disabled="loadingGeneration || !elementsToGenerateImage"
			ai-button
			@click-button="clickGenerate"
		/>
		<p class="m-20">{{getString('ai_images_generator_explain', [userStore.maxAIGeneratedImagesPerMonth])}}</p>
		<p class="mb-20" v-if="!loadingGeneration && !elementsToGenerateImage"><b>{{getString('no_generating_images')}}</b></p>
		<p class="mb-20" v-if="!loadingGeneration && elementsToGenerateImage > maxPicturesPerRequest">{{getString('ai_images_first_round', [maxPicturesPerRequest, elementsToGenerateImage - maxPicturesPerRequest])}}</p>
		<AppList v-if="!loadingGeneration && !!elementsToGenerateImage" :items="featuresListItems">
			<template #categories-options>
				<div v-if="!loadingGeneration && elementsToGenerateImage" class="row centered space-between">
					<p>{{ getString('select_categories') }}</p>
					<AppChecklist
						ref="categories-checklist"
						:title="getString('select')"
						:values="availableCategories.map(c => c.name)"
						:selected-indexes="categoriesSelectedIndexes"
						@change-checked="changeSelectedCategories"
					/>
				</div>
			</template>
			<template #theme-select="{item}">
				<div class="row centered space-between theme-select-row">
					<p>{{ item.title }}</p>
					<div class="container-theme-preview">
						<div class="theme-preview" v-if="selectedTheme" @click="toggleThemeBottomSheet">
							<div class="theme-preview-item">
								<img :src="selectedTheme.images[0]" alt="" />
							</div>
						</div>
						<AppButton :text="selectedTheme ? selectedTheme.label : getString('theme')" class="text-uppercase" outline @click="toggleThemeBottomSheet" />
						<AppBottomSheet :forceWidth="bottomSheetWidth" @toggling-popup="togglingThemeBottomSheet" :active="themeChooserBottomSheet">
							<div class="theme-container" v-if="themeChooserBottomSheet">
								<ModalHeader :title="getString('choose_theme')" />
								<div class="wrapper-content-bottom-sheet">
									<div class="wrapper-items-color-chooser">
										<div class="input-color-theme" v-for="(theme, index) in themeOptions" :key="index"
											 :class="{ active: selectedThemeIndex === index }" @click="selectTheme(index)">
											<p>{{ theme.label }}</p>
											<div class="container-slide">
												<div class="preview-color-slide" v-for="(img, imgIdx) in theme.images" :key="imgIdx">
													<img :src="img" :alt="getString('preview')"/>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</AppBottomSheet>
					</div>
				</div>
			</template>
			<template #advanced-options>
				<div>
					<AppList :items="advancedListItems"></AppList>
				</div>
			</template>
		</AppList>
		<div class="col-12 centered" v-if="loadingGeneration">
			<AppProgressBar v-if="waitingGeneration" :progress="completingPercent"/>
			<p v-show="loadingGeneration" class="mt-20 text-align-center">{{getString('generating_images')}}</p>
			<p v-show="waitingGeneration" class="mb-5 text-align-center">{{completingPercent}}%</p>
		</div>
		<div id="ai-images-tools-generation-anim" v-show="loadingGeneration"></div>
	</div>
</template>

<script>
import AppDropzone from "@/shared/components/AppDropzone.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import API from "@/shared/helpers/API";
import {useMenuStore, useUserStore} from "@/console-new/stores";
import {useNotificationStore, useCurrencyStore, useLanguageStore} from "@/shared/stores";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppList from "@/shared/components/AppList.vue";
import AppProgressBar from "@/shared/components/AppProgressBar.vue";
import AppChecklist from "@/shared/components/AppChecklist.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AiButton from "@/console-new/components/shared/AiButton.vue";

// MenuPictures::MAX_PER_REQUEST: the API takes the first ones and reports the rest as skipped
const MAX_PICTURES_PER_REQUEST = 200;
// The pictures arrive on Ably; the API is read when the channel attaches or loses continuity, and on this safety timer
const PICTURES_SAFETY_CHECK_MS = 60000;

export default {
	setup() {
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const menuStore = useMenuStore();
		const currencyStore = useCurrencyStore();
		const getString = useLanguageStore().getString;
		const themeOptions = [
			{
				id: "foodporn",
				label: getString('theme_foodporn'),
				images: ["https://img.qromo.io/ai-images/17495557432gE2zKqr17.png","https://img.qromo.io/ai-images/1749555761S7emGG2lqL.png","https://img.qromo.io/ai-images/1749555780ugdJExMcVn.png","https://img.qromo.io/ai-images/1749555800YfiUbRwboL.png"]
			},
			{
				id: "advposter",
				label: getString('theme_adv_poster'),
				images: ["https://img.qromo.io/ai-images/1749555803aEaTz4F7cb.png","https://img.qromo.io/ai-images/1749555818lMreRiThha.png","https://img.qromo.io/ai-images/1749555838XFYwdwUCA8.png","https://img.qromo.io/ai-images/1749555855EoXp7KApM4.png"]
			},
			{
				id: "luxglow",
				label: getString('theme_luxglow'),
				images: ["https://img.qromo.io/ai-images/17495558625vU0rfAoKg.png","https://img.qromo.io/ai-images/1749555873CHiCmUxaCh.png","https://img.qromo.io/ai-images/1749555893su6jAQ8ZZQ.png","https://img.qromo.io/ai-images/17495559172W2xJNXGmO.png"]
			},
			{
				id: "artisanal",
				label: getString('theme_artisanal'),
				images: ["https://img.qromo.io/ai-images/1749555941aL7OLn5xqV.png","https://img.qromo.io/ai-images/1749555959Hx68qcop3W.png","https://img.qromo.io/ai-images/1749555985uWawwXm3nk.png","https://img.qromo.io/ai-images/1749556007WjGZWkG4r9.png"]
			},
			{
				id: "neonwave",
				label: getString('theme_neonwave'),
				images: ["https://img.qromo.io/ai-images/1749555991Uhxl6tsTce.png","https://img.qromo.io/ai-images/1749556011P3TAklofhu.png","https://img.qromo.io/ai-images/1749556031Vy1N4Ndldp.png","https://img.qromo.io/ai-images/1749556042BCr4gDlJNP.png"]
			},
			{
				id: "cyberpunk",
				label: getString('theme_cyberpunk'),
				images: ["https://img.qromo.io/ai-images/1749556094Tertw1ge8e.png","https://img.qromo.io/ai-images/1749556118iMNs1hWYuU.png","https://img.qromo.io/ai-images/1749556138ygs5WYaoId.png","https://img.qromo.io/ai-images/1749556170hgjtxzPzLu.png"]
			},
			{
				id: "iconsmaterial",
				label: getString('theme_iconsmaterial'),
				images: ["https://img.qromo.io/ai-images/1749556169PhTXgz9dMU.png","https://img.qromo.io/ai-images/1749556185PrYviQUX0u.png","https://img.qromo.io/ai-images/1749556222b9nAVrnWG5.png","https://img.qromo.io/ai-images/1749556235OR9yokedZu.png"]
			},
			{
				id: "isometriclift",
				label: getString('theme_isometriclift'),
				images: ["https://img.qromo.io/ai-images/1749556258H25ji9rXwG.png","https://img.qromo.io/ai-images/1749556296essJW4SinS.png","https://img.qromo.io/ai-images/1749556313vkG4WMTcfs.png","https://img.qromo.io/ai-images/1749556323FxVJtrxtMb.png"]
			},
			{
				id: "vangogh",
				label: getString('theme_vangogh'),
				images: ["https://img.qromo.io/ai-images/1749556323wqIFYfFD8U.png","https://img.qromo.io/ai-images/17495563379mOA612rvS.png","https://img.qromo.io/ai-images/17495563780atJJ1fnbI.png","https://img.qromo.io/ai-images/17495573996eHRBdcuzc.png"]
			},
			{
				id: "roylichtenstein",
				label: getString('theme_roylichtenstein'),
				images:  ["https://img.qromo.io/ai-images/1749560495a2S2AJMa6w.png","https://img.qromo.io/ai-images/1749560512jApSjX7TMA.png","https://img.qromo.io/ai-images/1749560528eexLyNqPPV.png","https://img.qromo.io/ai-images/1749561031qx6dRp2Hua.png"]
			},
			{
				id: "ukiyoe",
				label: getString('theme_ukiyoe'),
				images: ["https://img.qromo.io/ai-images/1749558357iKxHMZ878P.png","https://img.qromo.io/ai-images/1749558374qZlIRoP7TR.png","https://img.qromo.io/ai-images/1749558388ew2QtLJeNu.png","https://img.qromo.io/ai-images/1749559354kIP0Qdbhg5.png"]
			},
			{
				id: "origami",
				label: getString('theme_origami'),
				images: ["https://img.qromo.io/ai-images/1749560557ycrkmDKo2K.png","https://img.qromo.io/ai-images/1749560569ou8joUyIgy.png","https://img.qromo.io/ai-images/1749560584GT6Tn0ZuLT.png","https://img.qromo.io/ai-images/1749560599OjTbmXBudK.png"]
			},
			{
				id: "watercolor",
				label: getString('theme_watercolor'),
				images: ["https://img.qromo.io/ai-images/17495574456kcTB6XhdI.png","https://img.qromo.io/ai-images/1749557461Be6VJchAft.png","https://img.qromo.io/ai-images/1749557483KW9pgPUicV.png","https://img.qromo.io/ai-images/1749557500rXd1aM0YUg.png"]
			},
			{
				id: "sketch",
				label: getString('theme_sketch'),
				images: ["https://img.qromo.io/ai-images/1749557504eMA8qiG11x.png","https://img.qromo.io/ai-images/1749558501IprUJfLfKB.png","https://img.qromo.io/ai-images/1749558520QfKmz5ozpo.png","https://img.qromo.io/ai-images/1749558535KtzIoOFLXv.png"]
			}
		];
		return {userStore, currencyStore, themeOptions, getString, showNotification: notificationStore.showNotification, menuStore};
	},
	name: "AIImagesTools",
	mixins: [WindowWidth],
	emits: ["close", "loading", "generated"],
	components: {AppProgressBar, AiButton, AppButton, ModalHeader, AppList, AppChecklist, AppDropzone, AppLoader, AppBottomSheet},
	data() {
		return {
			loadingGeneration: false,
			requestedPictures: 0,
			followingPictures: false,
			maxPicturesPerRequest: MAX_PICTURES_PER_REQUEST,
			pollingTimeout: null,
			customInfoText: null,
			selectedThemeIndex: 0,
			themeChooserBottomSheet: false,
			categoriesSelectedIndexes: [],
			selectedIndexes: {
				camera: 0,
				lighting: 0,
				surface: 0,
				dishware: 0,
				brand_vibe: 0
			},
			loadedAnimation: false,
			generationAnimation: null,
		};
	},
	computed: {
		waitingGeneration() {
			return this.menuStore.picturesRemaining;
		},
		buttonText() {
			return this.elementsToGenerateImage === 1 ? this.getString('generate_one_image') : this.getString('generate_x_images', [this.elementsToGenerateImage]);
		},
		availableCategories() {
			let categories = [];
			Object.values(this.menuStore.modMenu.categories).forEach((category) => {
				if(!category.variations_only && !category.additional_costs_shortcuts) {
					if(
						(Object.values(category.items).find(item => !item?.picture && item?.name && !item?.deleted_item)) ||
						(!category?.picture && category?.name)
					) {
						categories.push(category);
					}
				}
			});
			return categories;
		},
		completingPercent() {
			if(!this.waitingGeneration) {
				return 100;
			}
			let percent = 100 - Math.round((this.waitingGeneration / (this.requestedPictures || this.elementsToGenerateImage || 1)) * 100);
			percent = percent >= 100 ? 100 : percent;
			return percent <= 0 ? 0 : percent;
		},
		selectedTheme() {
			return this.selectedThemeIndex !== null ? this.themeOptions[this.selectedThemeIndex] : null;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		featuresListItems() {
			return [
				{
					title: this.getString('select_categories'),
					customSlot: "categories-options",
				},
				{
					title: this.getString('image_style'),
					customSlot: 'theme-select',
				},
				{
					title: this.getString('advanced_options'),
					chevronItem: true,
					active: false,
					dropdownSlot: 'advanced-options',
					backgroundColor: 'transparent'
				}
			]
		},
		advancedListItems() {
			return [
				{
					title: this.getString('camera'),
					selectedIndex: this.selectedIndexes.camera,
					callbackSelect: v => {
						this.selectedIndexes.camera = v;
					},
					selectValues: [this.getString('45_hero'), this.getString('top_down'), this.getString('straight_on'), this.getString('macro_close_up')],
				},
				{
					title: this.getString('lighting'),
					selectedIndex: this.selectedIndexes.lighting,
					callbackSelect: v => {
						this.selectedIndexes.lighting = v;
					},
					selectValues: [this.getString('soft_daylight'), this.getString('bright_studio'), this.getString('warm_tungsten'), this.getString('back_lit')],
				},
				{
					title: this.getString('brand_vibe'),
					selectedIndex: this.selectedIndexes.brand_vibe,
					callbackSelect: v => { this.selectedIndexes.brand_vibe = v; },
					selectValues: [this.getString('artisanal_vibe'), this.getString('futuristic_vibe'), this.getString('organic_vibe'), this.getString('playful_vibe')],
				},
				{
					title: this.getString('surface'),
					selectedIndex: this.selectedIndexes.surface,
					callbackSelect: v => { this.selectedIndexes.surface = v; },
					selectValues: [this.getString('plain_white'), this.getString('matte_marble'), this.getString('rustic_wood'), this.getString('dark_stone')],
				},
				{
					title: this.getString('dishware'),
					selectedIndex: this.selectedIndexes.dishware,
					callbackSelect: v => { this.selectedIndexes.dishware = v; },
					selectValues: [this.getString('classic_plate'), this.getString('black_stone'), this.getString('rustic_ceramic'), this.getString('iron_skillet'), this.getString('no_dish')],
				},
				{
					title: this.getString('additional_information'),
					placeholder: this.getString('additional_information_placeholder'),
					textInput: true,
					isTextArea: true,
					callbackText: v => { this.customInfoText = v; },
				},
			]
		},
		selectedCategories() {
			let categories = [];
			this.categoriesSelectedIndexes.forEach(index => {
				if(this.availableCategories[index]) {
					categories.push(this.availableCategories[index]);
				}
			});
			return categories;
		},
		elementsToGenerateImage() {
			if(!this.availableCategories?.length || !this.selectedCategories?.length) {
				return 0;
			}
			let elements = 0;
			this.selectedCategories.forEach(category => {
				if(!category?.picture && !!category?.name) {
					elements += 1;
				}
				if(category.items && Object.keys(category.items).length > 0) {
					for(let itemId in category.items) {
						let item = category.items[itemId];
						if(!item?.picture && !!item?.name) {
							elements += 1;
						}
					}
				}
			})
			if(elements > 0) {
				if(!!this.menuStore.modMenu?.name && !this.menuStore.modMenu?.picture) {
					elements += 1;
				}
				if(!!this.userStore.currBusiness?.name && !this.userStore.currBusiness?.picture) {
					elements += 1;
				}
			}
			return elements;
		}
	},
	methods: {
		togglingThemeBottomSheet(newVal) {
			this.themeChooserBottomSheet = !!newVal;
		},
		toggleThemeBottomSheet() {
			this.themeChooserBottomSheet = !this.themeChooserBottomSheet;
		},
		selectTheme(index) {
			this.selectedThemeIndex = index;
			this.toggleThemeBottomSheet();
		},
		changeSelectedCategories(indexes, newValues, checkedIndex) {
			if (!indexes?.length) {
				let checkList = this.$refs['categories-checklist'];
				if (checkList?.elementToggled) {
					checkList.elementToggled(true, checkedIndex);
					return;
				}
			}
			let categoriesIds = [];
			indexes.forEach(index => {
				if (this.availableCategories[index]) {
					categoriesIds.push(index);
				}
			});
			this.categoriesSelectedIndexes = categoriesIds;
		},
		startPolling() {
			this.loadingGeneration = true;
			this.$emit("loading", true);
			clearTimeout(this.pollingTimeout);
			this.pollingTimeout = setTimeout(this.fetchGeneratingItems, PICTURES_SAFETY_CHECK_MS);
		},
		followPictures() {
			if (!this.followingPictures) {
				this.followingPictures = true;
				this.menuStore.subscribeToPictureUpdates(this.fetchGeneratingItems);
			}
		},
		stopFollowingPictures() {
			if (this.followingPictures) {
				this.followingPictures = false;
				this.menuStore.unsubscribeFromPictureUpdates();
			}
		},
		fetchGeneratingItems() {
			this.getUpdates(success => {
				if (success) {
					this.stopFollowingPictures();
					this.loadingGeneration = !!this.waitingGeneration;
					this.menuStore.refreshMenusData();
				}
				this.$emit('close');
			})
		},
		getUpdates(callback) {
			this.loadingGeneration = true;
			this.$emit("loading", true);
			clearTimeout(this.pollingTimeout);
			API.init().getMenuAIImagesUpdates(this.userStore.currBusinessId, res => {
				if (res && res?.result === 'OK') {
					this.menuStore.picturesRemaining = Number(res?.remaining || 0);
					//this.loadingGeneration = !!this.waitingGeneration;
					if (!!this.waitingGeneration) {
						this.followPictures();
						this.pollingTimeout = setTimeout(this.fetchGeneratingItems, PICTURES_SAFETY_CHECK_MS);
					} else if (callback) {
						callback(true);
					}
				} else {
					this.showNotification("generic-fail");
					this.loadingGeneration = false;
					callback(false);
				}
			}, err => {
				this.showNotification("generic-fail");
				this.loadingGeneration = false;
				callback(false);
			});
		},
		clickGenerate() {
			if (!this.menuStore.modMenu?.menu_id || this.loadingGeneration) {
				return;
			}
			let categoriesIds = this.categoriesSelectedIndexes.map(index => this.availableCategories[index].menu_category_id);
			if (!categoriesIds?.length) {
				return;
			}
			this.loadGeneratingAnimation();
			this.loadingGeneration = true;
			let sendingData = {
				...this.selectedIndexes,
				custom_info: this.customInfoText
			}
			if (this.selectedThemeIndex !== null) {
				sendingData.theme = this.selectedTheme.id;
			}
			this.$emit("loading", true);
			this.getUpdates(success => {
				if (success) {
					this.menuStore.saveMenu(() => {
						API.init().generateMenuPictures(this.userStore.currBusinessId, this.menuStore.modMenu.menu_id, categoriesIds, sendingData, resp => {
							if (resp && resp?.result === 'OK') {
								this.requestedPictures = Number(resp?.requested || 0);
								this.menuStore.picturesRemaining = this.requestedPictures;
								this.followPictures();
								this.startPolling();
							} else {
								let notificationName = 'generic-fail';
								notificationName = resp?.details === 'rate_limit' ? 'ai-images-rate-limit' : notificationName;
								notificationName = resp?.details === 'too_many_pictures_requested' ? 'ai-images-too-many' : notificationName;
								this.showNotification(notificationName);
								this.loadingGeneration = false;
								this.$emit("loading", false);
							}
						});
					})
				} else {
					this.$emit("loading", false);
				}
			})
		},
		loadGeneratingAnimation() {
			if (typeof bodymovin !== 'undefined') {
				const animationPath = 'https://img.qromo.io/img/vue/anim/ai-generation' + (this.userStore.darkMode ? '-dark' : '-light') + '.json';
				if (!this.loadedAnimation) {
					this.generationAnimation = bodymovin.loadAnimation({
						container: document.getElementById('ai-images-tools-generation-anim'),
						renderer: 'svg',
						loop: true,
						autoplay: true,
						path: animationPath,
						rendererSettings: {
							preserveAspectRatio: 'xMidYMid meet'
						}
					});
					this.loadedAnimation = true;
				}
			}
		}
	},
	mounted() {
		let categoriesIds = [];
		this.availableCategories.forEach((category, index) => {
			categoriesIds.push(index)
		})
		this.categoriesSelectedIndexes = categoriesIds;
		this.loadGeneratingAnimation();
	},
	beforeUnmount() {
		if(this.generationAnimation?.destroy) {
			this.generationAnimation.destroy();
		}
		clearTimeout(this.pollingTimeout);
		this.stopFollowingPictures();
	},
	watch: {
		// The last picture of the round arrived on the channel: done without waiting for the next poll
		'menuStore.picturesRemaining'(remaining) {
			if (this.followingPictures && remaining === 0) {
				this.stopFollowingPictures();
				clearTimeout(this.pollingTimeout);
				this.loadingGeneration = false;
				// The pictures are already in the store; the preview is a page of its own and needs a reload
				this.$emit('generated');
				this.$emit('close');
			}
		}
	}
}
</script>

<style scoped lang="scss">
.upload-row {
	margin-top: 12px;
	margin-bottom: 12px;
	gap: 12px;
}
.result-row {
	border-bottom: 1px solid $primary;
	padding: 8px 0;
	gap: 12px;

	&:last-child {
		border-bottom: none;
	}

	.title {
		font-weight: 500;
		color: #9e9e9e;
		font-size: 14px;
	}
	.data {
		margin-left: auto;
		font-size: 14px;
	}
	.result {
		font-size: 16px;
		font-weight: 700;
	}
}
#ai-images-tools-generation-anim {
	width: 100%;
	max-width: 600px;
	margin: 0 auto;
	min-height: 250px;
	@media (max-width: 768px) {
		min-height: 210px;
	}
	@media (max-width: 480px) {
		min-height: 180px;
	}
	@media (max-width: 420px) {
		min-height: 150px;
	}
	@media (max-width: 380px) {
		min-height: 140px;
	}
}

.container-theme-preview {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	margin-left: 20px;
	cursor: pointer;

	@media (max-width: 400px) {
		margin-left: auto;
	}

	.theme-preview {
		width: 40px;
		height: 40px;
		margin-right: 8px;
		border-radius: 13px;
		border: 1px solid #cccccc;
		display: flex;
		align-items: center;
		justify-content: center;
		position: relative;
		flex: 1;
		flex-wrap: wrap;
		overflow: hidden;

		.theme-preview-item {
			width: 100%;
			height: 100%;

			&.theme-preview-left-top {
				border-top-left-radius: 12px;
			}

			&.theme-preview-right-top {
				border-top-right-radius: 12px;
			}

			&.theme-preview-left-bottom {
				border-bottom-left-radius: 12px;
			}

			&.theme-preview-right-bottom {
				border-bottom-right-radius: 12px;
			}

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}
	}

	.text-uppercase {
		text-transform: capitalize;
	}
}

.wrapper-content-bottom-sheet {
	margin-bottom: 30px;

	.wrapper-items-color-chooser {
		display: flex;
		flex-wrap: wrap;
		gap: 10px 40px;
		@media (max-width: 1080px) {
			gap: 10px;
		}

		.input-color-theme {
			display: flex;
			flex-direction: column;
			flex: 0 0 50%;
			max-width: calc(50% - 20px);
			padding: 10px;
			border-radius: 0;
			cursor: pointer;
			border: 1px solid transparent;
			@media (max-width: 1080px) {
				max-width: calc(50% - 5px);
			}
			@media (max-width: 768px) {
				flex: 0 0 100%;
				max-width: 100%;
			}

			&:hover {
				border: 1px solid transparent;
				background-color: $alpha_1;
				border-radius: 13px;
			}

			p {
				font-size: 22px;
				line-height: 26px;
				font-weight: 600;
				margin-bottom: 5px;
				margin-left: 10px;
				text-transform: capitalize;
			}

			.container-slide {
				height: 90px;
				width: 100%;
				display: flex;
				overflow: hidden;
				border-radius: 13px;
				box-shadow: 0 1px 2px var(--color-alpha-4);

				@media (max-width: 400px) {
					height: 60px;
				}

				.preview-color-slide {
					pointer-events: none;
					flex: 0 0 25%;
					max-width: 25%;
					height: 100%;
					display: flex;

					img {
						width: 100%;
						height: 100%;
						object-fit: cover;
					}
				}
			}

			&.active {
				border: 1px solid $primary;
				background-color: $alpha_1;
				border-radius: 13px;
			}
		}
	}
}
</style>